<script setup>
// O formulário de WhatsApp, dentro da gaveta (título no cabeçalho, Criar na gaveta). Duas seções que abrem e fecham
// (8-6 = A) e, sob os parâmetros do template, a pré-visualização de como a mensagem chega (8-4 = B). Validação,
// `prepareCampaignDetails` e o evento `submit` são os do woot.
import { reactive, computed, watch, ref, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import CampaignTemplatePreview from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/CampaignTemplatePreview.vue';
import FoldingGroup from 'dashboard/components-next/polaris/cards/FoldingGroup.vue';
import MultiSelectInput from 'dashboard/components-next/polaris/cards/MultiSelectInput.vue';
import SearchSelect from 'dashboard/components-next/polaris/cards/SearchSelect.vue';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from 'dashboard/components-next/polaris/ui/field';
import { Input } from 'dashboard/components-next/polaris/ui/input';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';

const emit = defineEmits(['submit']);
const title = defineModel('title', { type: String, default: '' });

const { t } = useI18n();
const uid = useId();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
};

const initialState = {
  title: '',
  inboxId: null,
  templateId: null,
  scheduledAt: null,
  selectedAudience: [],
};

const state = reactive({ ...initialState });
const baseline = ref(JSON.stringify(initialState));
const templateParserRef = ref(null);

const rules = {
  title: { required, minLength: minLength(1) },
  inboxId: { required },
  templateId: { required },
  scheduledAt: { required },
  selectedAudience: { required },
};

const v$ = useVuelidate(rules, state);

const isSaving = computed(() => formState.uiFlags.value.isCreating);
const isDirty = computed(() => JSON.stringify(state) !== baseline.value);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  const templates = formState.getFilteredWhatsAppTemplates.value(state.inboxId);
  return templates.map(template => {
    // Create a more user-friendly label from template name
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template,
    };
  });
});

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(option => option.value === state.templateId)
    ?.template;
});

const formErrors = computed(() => ({
  title: v$.value.title.$error
    ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.ERROR')
    : '',
  inbox: v$.value.inboxId.$error
    ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.ERROR')
    : '',
  template: v$.value.templateId.$error
    ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.ERROR')
    : '',
  scheduledAt: v$.value.scheduledAt.$error
    ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.ERROR')
    : '',
  audience: v$.value.selectedAudience.$error
    ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.ERROR')
    : '',
}));

const hasRequiredTemplateParams = computed(() => {
  return templateParserRef.value?.v$?.$invalid === false || true;
});

const isSubmitDisabled = computed(
  () => v$.value.$invalid || !hasRequiredTemplateParams.value
);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, initialState);
  baseline.value = JSON.stringify(initialState);
  v$.value.$reset();
};

const prepareCampaignDetails = () => {
  // Find the selected template to get its content
  const currentTemplate = selectedTemplate.value;
  const parserData = templateParserRef.value;

  // Extract template content - this should be the template message body
  const templateContent = parserData?.renderedTemplate || '';

  // Prepare template_params object with the same structure as used in contacts
  const templateParams = {
    name: currentTemplate?.name || '',
    namespace: currentTemplate?.namespace || '',
    category: currentTemplate?.category || 'UTILITY',
    language: currentTemplate?.language || 'en_US',
    processed_params: parserData?.processedParams || {},
  };

  return {
    title: state.title,
    message: templateContent,
    template_params: templateParams,
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience: state.selectedAudience?.map(id => ({
      id,
      type: 'Label',
    })),
  };
};

// Valida e entrega os dados; o Criar da gaveta chama isto. Devolve se havia o que entregar.
const submit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return false;

  emit('submit', prepareCampaignDetails());
  return true;
};

// O título é o campo do cabeçalho da gaveta: dois sentidos, sem laço.
watch(
  title,
  value => {
    if (value !== state.title) state.title = value;
  },
  { immediate: true }
);
watch(
  () => state.title,
  value => {
    if (value !== title.value) title.value = value;
  }
);

// Reset template selection when inbox changes
watch(
  () => state.inboxId,
  () => {
    state.templateId = null;
  }
);

defineExpose({
  prepareCampaignDetails,
  isSubmitDisabled,
  isDirty,
  isSaving,
  submit,
  resetState,
  state,
});
</script>

<template>
  <form
    class="grid gap-6"
    novalidate
    data-campaign-form="whatsapp"
    @submit.prevent="submit"
  >
    <FieldError
      v-if="formErrors.title"
      :errors="[formErrors.title]"
      data-campaign-title-error
    />

    <FoldingGroup :title="t('CAMPAIGN.DRAWER.SECTIONS.MESSAGE')">
      <FieldGroup class="gap-4">
        <Field :data-invalid="!!formErrors.inbox">
          <FieldLabel :for="`${uid}-inbox`">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
          </FieldLabel>
          <SearchSelect
            :id="`${uid}-inbox`"
            v-model="state.inboxId"
            :options="inboxOptions"
            :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL')"
            :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
            :aria-invalid="!!formErrors.inbox"
          />
          <FieldError v-if="formErrors.inbox" :errors="[formErrors.inbox]" />
        </Field>

        <Field :data-invalid="!!formErrors.template">
          <FieldLabel :for="`${uid}-template`">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
          </FieldLabel>
          <SearchSelect
            :id="`${uid}-template`"
            v-model="state.templateId"
            :options="templateOptions"
            :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL')"
            :placeholder="
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')
            "
            :aria-invalid="!!formErrors.template"
          />
          <FieldDescription>
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.INFO') }}
          </FieldDescription>
          <FieldError
            v-if="formErrors.template"
            :errors="[formErrors.template]"
          />
        </Field>

        <template v-if="selectedTemplate">
          <WhatsAppTemplateParser
            ref="templateParserRef"
            hide-preview
            :template="selectedTemplate"
          />
          <CampaignTemplatePreview
            :template="selectedTemplate"
            :body="templateParserRef?.renderedTemplate"
          />
        </template>
      </FieldGroup>
    </FoldingGroup>

    <FoldingGroup :title="t('CAMPAIGN.DRAWER.SECTIONS.AUDIENCE_SEND')">
      <FieldGroup class="gap-4">
        <Field :data-invalid="!!formErrors.audience">
          <FieldLabel :for="`${uid}-audience`">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
          </FieldLabel>
          <MultiSelectInput
            :id="`${uid}-audience`"
            v-model="state.selectedAudience"
            :options="audienceList"
            :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
            :placeholder="
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')
            "
            :aria-invalid="!!formErrors.audience"
          />
          <FieldError
            v-if="formErrors.audience"
            :errors="[formErrors.audience]"
          />
        </Field>

        <Field :data-invalid="!!formErrors.scheduledAt">
          <FieldLabel :for="`${uid}-scheduled`">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL') }}
          </FieldLabel>
          <Input
            :id="`${uid}-scheduled`"
            v-model="state.scheduledAt"
            type="datetime-local"
            :min="currentDateTime"
            :placeholder="
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')
            "
            :aria-invalid="!!formErrors.scheduledAt"
            @blur="v$.scheduledAt.$touch()"
          />
          <FieldError
            v-if="formErrors.scheduledAt"
            :errors="[formErrors.scheduledAt]"
          />
        </Field>
      </FieldGroup>
    </FoldingGroup>
  </form>
</template>
